<template>
  <div class="homework-generator">
    <div class="generator-header">
      <h2>个性化作业生成</h2>
      <el-button type="primary" @click="back">返回</el-button>
    </div>

    <div class="generator-content">
      <div class="settings-panel">
        <div class="settings-title">作业设置</div>
        <div class="settings-form">
          <div class="form-item">
            <label>年级选择</label>
            <el-select v-model="selectedGrade" placeholder="请选择年级">
              <el-option
                v-for="grade in grades"
                :key="grade"
                :label="grade"
                :value="grade">
              </el-option>
            </el-select>
          </div>
          <div class="form-item">
            <label>科目选择</label>
            <el-select v-model="selectedSubject" placeholder="请选择科目">
              <el-option
                v-for="subject in subjects"
                :key="subject"
                :label="subject"
                :value="subject">
              </el-option>
            </el-select>
          </div>
          <div class="form-item">
            <label>知识点</label>
            <el-input
              v-model="knowledgePoints"
              type="textarea"
              :rows="3"
              placeholder="请输入需要包含的知识点，多个知识点用逗号分隔">
            </el-input>
          </div>
          <div class="form-item action-buttons">
            <el-button type="primary" @click="generateHomework" :loading="loading">生成作业</el-button>
            <el-dropdown @command="handleDownload" :disabled="!aiResponse">
              <el-button type="success" :disabled="!aiResponse">
                下载作业<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="markdown">下载为Markdown</el-dropdown-item>
                <el-dropdown-item command="word">下载为Word</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>
      </div>

      <div class="chat-panel">
        <div class="chat-container" :class="{ 'chat-active': messages.length > 0 }">
          <ChatArea :messages="messages" :loading="loading" @suggest-question="handleSuggestedQuestion"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatArea from '@/views/ai/components/ChatArea.vue'
import { textCompletion } from '@/api/ai' // 导入后端AI接口
import { generateExam } from '@/api/homework' // 导入生成Word文档的API
import { marked } from 'marked' // 导入marked用于Markdown渲染

// 配置marked以支持Markdown
marked.setOptions({
  renderer: new marked.Renderer(),
  // 删除highlight函数中的LaTeX处理部分
  highlight: function(code, lang) {
    return code
  },
  pedantic: false,
  gfm: true,
  breaks: true,
  sanitize: false,
  smartLists: true,
  smartypants: false,
  xhtml: false
})

export default {
  name: 'createComponent',
  components: {
    ChatArea
  },
  data() {
    return {
      // UI 状态
      loading: false,
      downloading: false,

      // 用户选择
      selectedGrade: '一年级',
      selectedSubject: '语文',
      knowledgePoints: '', // 添加知识点输入

      // 聊天数据
      messages: [],
      modelInfo: '后端AI服务', // 修改模型信息
      aiResponse: '', // 存储AI响应，用于下载

      // 下拉选项
      grades: ['一年级', '二年级', '三年级', '四年级', '五年级', '六年级'],
      subjects: ['语文', '数学', '英语']
    }
  },
  mounted() {
    // 检查URL参数，如果有知识点参数且autoAnalyze为true，则自动发送解析请求
    const { knowledge, autoAnalyze } = this.$route.query
    if (knowledge && autoAnalyze === 'true') {
      // 将知识点填入输入框
      this.knowledgePoints = knowledge

      // 延迟一点时间再自动发送请求，确保组件已完全加载
      this.$nextTick(() => {
        // 构建解析知识点的提示语
        const prompt = `请详细解析「${knowledge}」知识点，包括概念解释、重点难点、常见错误以及解题技巧。`

        // 自动发送解析请求
        this.sendMessage(prompt)
      })
    }
  },
  updated() {

  },
  beforeDestroy() {

  },
  computed: {
    formattedResponse() {
      if (!this.aiResponse) return ''
      // 使用 marked 将 markdown 格式转换为 HTML
      try {
        // 预处理Markdown内容，特别处理数学公式部分
        let content = this.aiResponse

        // 应用Markdown转换
        const htmlContent = marked(content)

        return htmlContent
      } catch (e) {
        console.error('Markdown转换失败:', e)
        return this.aiResponse
      }
    }
  },
  methods: {
    // 添加返回方法
    back() {
      this.$router.go(-1) // 返回上一页
    },

    // 测试API连接
    async testConnection() {
      if (this.loading) return

      this.loading = true
      try {
        const result = await textCompletion('这是一个API连接测试，请简短回复。')
        if (result.code === 1) {
          this.messages.push({
            role: 'assistant',
            content: `连接测试成功: ${result.data || result.msg}`,
            isSystem: true
          })
        } else {
          throw new Error(result.msg || '连接测试失败')
        }
      } catch (error) {
        this.messages.push({
          role: 'assistant',
          content: `连接测试失败: ${error.message}`,
          isError: true,
          isSystem: true
        })
      } finally {
        this.loading = false
      }
    },

    // 处理建议问题
    handleSuggestedQuestion(question) {
      this.sendMessage(question)
    },

    // 生成作业
    generateHomework() {
      let prompt = `请为${this.selectedGrade}${this.selectedSubject}生成一份个性化作业`

      // 如果有知识点，添加到提示中
      if (this.knowledgePoints.trim()) {
        prompt += `，包含以下知识点：${this.knowledgePoints.trim()}`
      }

      prompt += '。请生成10道题目，包括题目和答案解析。'

      this.sendMessage(prompt)
    },

    async sendMessage(content) {
      if (!content.trim()) return

      // 添加用户消息
      this.messages.push({
        role: 'user',
        content
      })

      this.loading = true

      try {
        // 使用textCompletion替代sendChatMessage
        const result = await textCompletion(content)

        let aiResponse = ''

        // 处理不同的响应格式
        if (result.code === 1) {
          aiResponse = result.data
        } else if (result.code === 200) {
          aiResponse = result.data || result.result || result.message || ''
        } else if (typeof result === 'string') {
          aiResponse = result
        } else {
          throw new Error(result.msg || '请求失败')
        }

        // 存储AI响应用于下载
        this.aiResponse = aiResponse

        // 添加 AI 回复
        this.messages.push({
          role: 'assistant',
          content: aiResponse
        })
      } catch (error) {
        console.error('API 请求失败:', error)

        // 添加错误消息
        this.messages.push({
          role: 'assistant',
          content: `抱歉，发生了错误: ${error.message || '请稍后再试'}`,
          isError: true
        })
      } finally {
        this.loading = false
      }
    },

    // 处理下载命令
    handleDownload(command) {
      if (command === 'markdown') {
        this.downloadMarkdown()
      } else if (command === 'word') {
        this.downloadWord()
      }
    },

    // 下载为Markdown文件
    downloadMarkdown() {
      const fileName = `${this.selectedGrade}${this.selectedSubject}作业.md`
      const content = this.aiResponse

      // 创建Blob对象
      const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' })

      // 创建下载链接
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = fileName

      // 触发下载
      document.body.appendChild(link)
      link.click()

      // 清理
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)

      this.$message.success('Markdown文件下载成功')
    },

    // 下载为Word文档
    async downloadWord() {
      if (!this.aiResponse) {
        this.$message.warning('请先生成个性化作业内容');
        return;
      }

      try {
        this.downloading = true;

        // 处理内容，去除Markdown格式符号
        const plainTextContent = this.removeMarkdownFormat(this.aiResponse);

        // 准备请求数据
        const formData = new FormData();
        formData.append('title', '个性化作业');
        formData.append('content', plainTextContent);
        formData.append('type', 'homework');

        // 调用后端生成Word文档的接口
        const response = await generateExam(formData);

        // 处理下载 - 后端返回的是Blob数据
        if (response) {
          // response本身就是Blob对象，不需要再包装
          const blob = response;
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `个性化作业_${new Date().getTime()}.doc`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          this.$message.success('Word文档下载成功');
        } else {
          this.$message.error('文档生成失败，请重试');
        }
      } catch (error) {
        console.error('下载Word文档失败:', error);
        this.$message.error('下载失败: ' + (error.response?.data?.message || error.message || '未知错误'));
      } finally {
        this.downloading = false;
      }
    },

    // 去除Markdown格式符号，保留纯文本
    removeMarkdownFormat(content) {
       if (!content) return ''

       return content
         // 去除标题符号 (# ## ### 等)
         .replace(/^#{1,6}\s+/gm, '')
         // 去除粗体符号 (**text** 或 __text__)
         .replace(/\*\*(.*?)\*\*/g, '$1')
         .replace(/__(.*?)__/g, '$1')
         // 去除斜体符号 (*text* 或 _text_)
         .replace(/\*(.*?)\*/g, '$1')
         .replace(/_(.*?)_/g, '$1')
         // 去除删除线符号 (~~text~~)
         .replace(/~~(.*?)~~/g, '$1')
         // 去除行内代码符号 (`code`)
         .replace(/`([^`]+)`/g, '$1')
         // 去除代码块符号 (```)
         .replace(/```[\s\S]*?```/g, (match) => {
           return match.replace(/```\w*\n?/g, '').replace(/```/g, '')
         })
         // 去除无序列表符号 (- * +)
         .replace(/^[\s]*[-*+]\s+/gm, '')
         // 去除有序列表符号 (1. 2. 等)
         .replace(/^[\s]*\d+\.\s+/gm, '')
         // 去除引用符号 (>)
         .replace(/^>\s*/gm, '')
         // 去除链接格式 [text](url)
         .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
         // 去除图片格式 ![alt](url)
         .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
         // 去除水平分割线 (--- 或 ***)
         .replace(/^[-*]{3,}$/gm, '')
         // 清理多余的空行
         .replace(/\n\s*\n\s*\n/g, '\n\n')
         // 去除行首行尾的空白字符
         .trim()
     }
  }
}
</script>

<style scoped>
.homework-generator {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #f9fafb;
}

.generator-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.generator-content {
  display: flex;
  flex: 1;
  padding: 20px;
  gap: 20px;
  overflow: hidden; /* 防止内容溢出 */
}

.settings-panel {
  width: 300px;
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 20px; /* 距离顶部的距离 */
  height: fit-content;
  max-height: calc(100vh - 120px); /* 最大高度，防止超出视窗 */
  overflow-y: auto; /* 内容过多时可滚动 */
}

.settings-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #1f2937;
}

.settings-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-item label {
  font-size: 14px;
  color: #4b5563;
}

.chat-panel {
  flex: 1;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: calc(100vh - 120px); /* 设置固定高度 */
}

.chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 20px;
  overflow-y: auto; /* 允许内容滚动 */
}

.chat-bottom {
  margin-top: 16px;
}

.model-info {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 8px;
  text-align: center;
}

.chat-disclaimer {
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
  margin-top: 8px;
}

.chat-active {
  animation: fadeIn 0.5s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0.7;
  }
  to {
    opacity: 1;
  }
}

/* 确保滚动条样式一致 */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
